import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ticketDevicesQuery, type TicketDeviceBox, type TicketLicense } from '../api/easeedesk'
import { forceAgentUpdate, forceUpdateStatus } from '../api/devices'
import { Spinner } from '../ui/Spinner'
import { agoOrEmpty } from './format'

/**
 * Panoul "User Devices" al rezumatului -
 * `easeedesk/tickets/details/summary/owners_and_devices.php`, cu cele doua
 * cutii de masina (`device_box.php` si `deprecated_device_box.php`).
 *
 * Se cere DUPA ce fisa e pe ecran, ca in portal: e partea grea a rezumatului si
 * nimic din ea nu e nevoie ca sa se citeasca tichetul. Cat se asteapta, locul
 * arata ca se lucreaza - un dreptunghi gol care se umple dintr-odata se citeste
 * ca "nu exista echipamente", si apoi omul e luat prin surprindere cand apar.
 *
 * La esec locul nu ramane invartindu-se: se scrie ca n-au putut fi incarcate.
 * O rotita vesnica e o minciuna, restul fisei e intreg si omul trebuie sa stie
 * ca doar bucata asta lipseste.
 */

export function TicketDevices({ id }: { id: number }) {
  const { data, error } = useQuery(ticketDevicesQuery(id))

  if (error) {
    return (
      <div className="note note-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> Devices could not be loaded.
      </div>
    )
  }
  if (!data) return <Spinner />
  if (data.empty) return null

  const affected = data.affected

  return (
    <div className="tk-devices-owners" data-ticket={id}>
      <div>
        <h4>
          <i className="fa fa-angle-up toggle-details" aria-hidden="true"></i> User Devices
        </h4>
        <hr className="dotted" style={{ marginTop: 5 }} />
        <div className="slide-details">
          <table className="details-table" width="100%">
            <tbody>
              {affected.type === 'Devices' ? (
                <tr>
                  <td>
                    {affected.devices.map((device) => (
                      <DeviceBox key={device.id} device={device} />
                    ))}
                  </td>
                </tr>
              ) : (
                <>
                  <tr>
                    <td className="title">{affected.type}</td>
                    <td>
                      <span className="label label-danger">
                        <i className="fa fa-user" aria-hidden="true"></i> {affected.name}
                      </span>
                    </td>
                  </tr>
                  {affected.devices.length > 0 && (
                    <tr>
                      <td className="title">Devices</td>
                      <td>
                        {affected.devices.map((device) => (
                          <DeviceBox key={device.id} device={device} />
                        ))}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/**
 * O masina.
 *
 * Verde cu ochiul deschis sub doua ore de la ultima vorba, rosu cu ochiul taiat
 * peste - `$seconds < 7200` din macheta. Cand agentul e mai vechi decat cere
 * furnizorul, cutia capata si jumatatea din dreapta, cu butonul de actualizare.
 *
 * Butonul cere capul NOU (`POST /api/v2/devices/{id}/agent-settings/force-update`),
 * nu `overview/deviceForceUpdate` din macheta veche: metoda aceea a plecat odata
 * cu `Overview.php`, deci un `<form action>` catre ea ar fi trimis omul intr-un
 * 404. Markupul ramane cel de acolo, ca sa arate la fel.
 *
 * Starea "waiting to update.." se intreaba din 10 in 10 secunde, ca in
 * `owners_and_devices-1.js` (`ForceUpdate._checkStatus`): butonul sta rosu si
 * legat cat e o cerere deschisa, si se dezleaga singur cand agentul a ajuns la
 * versiunea ceruta - fara sa reincarce omul tichetul.
 */
const FORCE_UPDATE_POLL_MS = 10000

function useForceUpdateStatus(id: number, outdated: boolean, kicked: boolean): boolean {
  const [updating, setUpdating] = useState(false)
  useEffect(() => {
    if (!outdated) return
    let gone = false
    const ask = () => {
      void forceUpdateStatus(id)
        .then((r) => {
          if (!gone) setUpdating(r.status === 'updating')
        })
        .catch(() => undefined)
    }
    ask()
    const t = setInterval(ask, FORCE_UPDATE_POLL_MS)
    return () => {
      gone = true
      clearInterval(t)
    }
  }, [id, outdated, kicked])
  return updating
}

function DeviceBox({ device }: { device: TicketDeviceBox }) {
  const update = useMutation({ mutationFn: () => forceAgentUpdate(device.id, device.forceUpdateVersion ?? '') })
  const tone = device.live ? 'label-success' : 'label-danger'
  const eye = device.live ? 'fa-eye' : 'fa-eye-slash'
  const outdated = device.forceUpdateVersion !== null && device.forceUpdateVersion !== ''
  const edge = device.live ? '#5cb85c' : '#d9534f'
  const updating = useForceUpdateStatus(device.id, outdated, update.isSuccess) || update.isPending

  return (
    <div style={{ display: 'inline-block' }}>
      <div className="pull-left">
        <span data-id={device.id} className={`quick-sidebar-device label ${tone}`} style={{ cursor: 'pointer', borderRadius: 0 }}>
          <i className={`fa ${eye}`} aria-hidden="true"></i> {device.name}
          <div className="details" style={{ borderRadius: 0 }}>
            <i className="fa fa-podcast" aria-hidden="true"></i> {agoOrEmpty(device.lastActivity)}
          </div>
          <div className="details color-danger real-time-connection" style={{ borderRadius: 0 }}>
            <del>
              <p>
                <i className="fas fa-bolt" aria-hidden="true"></i> {outdated ? 'Real time connection not available' : 'Real time communication'}
              </p>
            </del>
          </div>
        </span>
      </div>
      {outdated && (
        <div className="pull-right">
          <span
            data-id={device.id}
            className={`label ${tone}`}
            style={{ cursor: 'pointer', background: 'none', borderRadius: 0, borderTop: `1px solid ${edge}`, borderBottom: `1px solid ${edge}`, borderRight: `1px solid ${edge}` }}
          >
            <form
              style={{ display: 'inline-block' }}
              method="post"
              className="forceUpdateForm"
              onSubmit={(ev) => {
                ev.preventDefault()
                update.mutate()
              }}
            >
              <input type="hidden" name="device" value={device.id} />
              <input type="hidden" name="new_version" value={device.forceUpdateVersion ?? ''} />
              <button className={`btn btn-xs ${updating ? 'btn-danger' : 'btn-success'}`} type="submit" style={{ margin: 2.5 }} disabled={updating}>
                <i className="fas fa-desktop"></i>
              </button>
              <p style={{ color: updating ? '#d9534f' : '#5cb85c', marginTop: 1 }} className="force-update-text">
                {updating ? 'waiting to update..' : 'update'}
              </p>
            </form>
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * Chipul de abonament - randul "Subscription" din rezumat.
 *
 * Exportat separat: il foloseste si fisa, cand rezumatul se muta. Culoarea si
 * cuvintele vin de la server, ca sa fie scrise o data (`SupportLicense::chip`).
 */
export function LicenseChip({ chip }: { chip: { tone: string; text: string } | null }) {
  if (!chip) return null

  return <span className={`label label-${chip.tone}`}>{chip.text}</span>
}

export type { TicketLicense }
